import { AxiosResponse } from 'axios';
import { Device } from '../types/Device';
import { PaginatedResult } from '../types/PaginatedResult';
import { httpGet, uploadFiles } from './api.service';
import { DeviceImportSummary } from '../types/device-import-summary';

interface SuperSentinelImportDetail {
  id: string;
  createdDateTime: string;
  approximateEndDateTime: string;
  totalDevices: number;
  statusSummary: {
    total: number;
    pending: number;
    processing: number;
    processed: number;
    error: number;
  };
  devices: Device[];
  userName: string | null;
}

// ---------------------------------------------------------------------------
// Friendly file-type error
// ---------------------------------------------------------------------------

const ACCEPTED_EXTENSIONS = '.csv';

export const getFriendlyFileTypeError = (file: File): string | null => {
  const name = file.name.toLowerCase();
  const accepted = ['.csv'];
  if (!accepted.some((ext) => name.endsWith(ext))) {
    return `Unsupported file type "${file.name.split('.').pop()}". ` + `Please upload one of the following formats: ${ACCEPTED_EXTENSIONS}.`;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

/**
 * Super Sentinel Bulk Upload
 *
 * Pre-processes the CSV to:
 *  1. Validate & normalise required column headers (# of days) and optional
 *     DeviceId / SerialNumber columns — case-insensitive + alias matching.
 *  2. Require at least DeviceId or SerialNumber.
 *  3. Deduplicate rows by DeviceId, SerialNumber, or both when both are provided.
 *  4. Reject empty files with a clear error message.
 */
export const uploadSuperSentinelBulkActivation = async (
  file: File,
  onUploadProgress: (progressEvent: ProgressEvent) => void,
): Promise<DeviceImportSummary> => {
  // --- File-type guard (friendly message) ---
  const fileTypeError = getFriendlyFileTypeError(file);
  if (fileTypeError) {
    throw new Error(fileTypeError);
  }

  const response: AxiosResponse = await uploadFiles(`api/device/supersentinel/bulk/upload`, { fileProcess: file }, onUploadProgress);

  if (response && response.status === 200) {
    return response.data;
  } else {
    // Surface the server's error message when available
    const serverMessage: string | undefined = response?.data?.message ?? response?.data?.error ?? response?.data?.title;
    throw new Error(serverMessage ?? 'Failed to upload Super Sentinel bulk activation file');
  }
};

/**
 * Get Super Sentinel Bulk Import Details
 */
export const getSuperSentinelBulkImportDetails = () => {
  return async (params: any): Promise<PaginatedResult<Device>> => {
    const response = await httpGet<PaginatedResult<Device>>(`api/device/supersentinel/bulk/`, params);
    return response;
  };
};

export const getSuperSentinelImportDetails = (id: string) => {
  return async (params: any): Promise<SuperSentinelImportDetail> => {
    const response = await httpGet<SuperSentinelImportDetail>(`api/device/supersentinel/bulk/${id}`, params);
    return response;
  };
};